import { Inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { empty, Observable, of, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { Logger, LoggerFactory } from './../../../api';
import { WchDynamicLoggerFactory, WchLoggerFactory } from './../../../ng-api';
import {
  createConsoleLogger,
  createEmptyLogger,
  executeLater,
  isNotNil,
  safeUnsubscribe
} from './../../../utils';
import { DEV_MODE_SUBJECT, isDevModeOn } from './../../internal/assert';

/* Copyright IBM Corp. 2017 */

const _LOGGERS = {};

// our default depending on the environment
const CREATE_CONSOLE_LOGGER = createConsoleLogger;

const CREATE_EMPTY_LOGGER = createEmptyLogger;

// instance of the logger factory in case of an override
let _loggerDelegateOverride;

// true if we have an override, else false
let _hasLoggerDelegateOverride = false;

function _getLoggerDelegate() {
  return (
    _loggerDelegateOverride ||
    (isDevModeOn() ? CREATE_CONSOLE_LOGGER : CREATE_EMPTY_LOGGER)
  );
}

function _getLogger(aName) {
  return _LOGGERS[aName] || (_LOGGERS[aName] = _getLoggerDelegate()(aName));
}

/**
 * Tests if logging is enabled
 *
 * @return true if logging is enabled, else false
 */
function _isLogging(): boolean {
  // either we have a delegate or we run in dev mode and log to the console
  return _hasLoggerDelegateOverride || isDevModeOn();
}

function _logInfo(aType: string, ...aArgs: any[]): void {
  // dispatch
  if (_isLogging()) {
    // dispatch
    const logger = _getLogger(aType);
    logger.info.apply(logger, aArgs);
  }
}

function _logError(aType: string, ...aArgs: any[]): void {
  // dispatch
  const logger = _getLogger(aType);
  logger.error.apply(logger, aArgs);
}

function _logWarn(aType: string, ...aArgs: any[]): void {
  // dispatch
  if (_isLogging()) {
    // dispatch
    const logger = _getLogger(aType);
    logger.warn.apply(logger, aArgs);
  }
}

function _logDeferred(aFunction: Function, aArgs: IArguments) {
  // dispatch
  if (_isLogging()) {
    // defer execution
    executeLater(() => aFunction.apply(undefined, aArgs));
  }
}

function _logInfoDeferred(aType: string, ...aArgs: any[]): void {
  // dispatch
  _logDeferred(_logInfo, arguments);
}

function _logErrorDeferred(aType: string, ...aArgs: any[]): void {
  // dispatch
  _logDeferred(_logError, arguments);
}

function _logWarnDeferred(aType: string, ...aArgs: any[]): void {
  // dispatch
  _logDeferred(_logWarn, arguments);
}

function _refreshLoggers() {
  // get the delegate
  const delegate = _getLoggerDelegate();
  // replace the loggers
  // tslint:disable-next-line:forin
  for (const name in _LOGGERS) {
    _LOGGERS[name] = delegate(name);
  }
}

// register for changes of the dev mode flag
const _modeSubscription = DEV_MODE_SUBJECT.subscribe(mode => {
  // we only need to refresh if there was not explicit override
  if (_hasLoggerDelegateOverride) {
    _refreshLoggers();
  }
});

/**
 * Implementation of the logger service. The service accepts either a {@link LoggerFactory} or a {@link DynamicLoggerFactory}
 * as initialization parameters to dynamically replace logger instances.
 */
@Injectable({ providedIn: 'root' })
export class LoggerProxy implements OnDestroy {
  // subscription to the logger factories
  private _fctSubscription: Subscription;

  constructor(@Inject(Injector) aInjector: Injector) {
    // access the dependencies
    const factory = aInjector.get(WchLoggerFactory, null);
    const dynamicFactory = aInjector.get(WchDynamicLoggerFactory, null);
    // check the flags
    const bFactory = isNotNil(factory),
      bDynamicFactory = isNotNil(dynamicFactory);
    // subscribe to the loggers
    const onLoggerFactories: Observable<LoggerFactory> = bFactory
      ? bDynamicFactory
        ? dynamicFactory.get().pipe(startWith(factory))
        : of(factory)
      : bDynamicFactory
        ? dynamicFactory.get()
        : empty();
    // replace the loggers
    this._fctSubscription = onLoggerFactories.subscribe(fct => {
      // replace the logger delegate
      _loggerDelegateOverride = (name: string) => fct.create(name);
      _hasLoggerDelegateOverride = true;
      // replace the loggers
      _refreshLoggers();
    });
  }

  get(name: string): Logger {
    return _getLogger(name);
  }

  ngOnDestroy() {
    // unsubscribe
    safeUnsubscribe(_modeSubscription);
    safeUnsubscribe(this._fctSubscription);
  }
}

export {
  _logInfo as li,
  _logWarn as lw,
  _logError as le,
  _logInfoDeferred as lid,
  _logWarnDeferred as lwd,
  _logErrorDeferred as led
};
